import { Wrench } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useVehicles } from "@/hooks/useVehicles";
import { useMaintenance, MaintenanceTask } from "@/hooks/useMaintenance";
import { VehicleMaintenanceTimeline } from "@/components/maintenance/VehicleMaintenanceTimeline";
import { MaintenanceTaskFormValues } from "@/components/maintenance/MaintenanceTaskDialog";

const Maintenance = () => {
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();
  const { 
    tasks, 
    isLoading: tasksLoading, 
    addTask, 
    updateTask,
    deleteTask,
    toggleComplete,
    uploadAttachment 
  } = useMaintenance();

  const isLoading = vehiclesLoading || tasksLoading;

  const getTasksForVehicle = (vehicleId: string): MaintenanceTask[] => {
    return tasks?.filter((task) => task.vehicle_id === vehicleId) || [];
  };

  const handleAddTask = (
    vehicleId: string, 
    values: MaintenanceTaskFormValues, 
    attachments: string[]
  ) => {
    addTask.mutate({
      vehicle_id: vehicleId,
      task_name: values.task_name,
      due_date: values.due_date || undefined,
      due_at_mileage: values.due_at_mileage || undefined,
      description: values.description || undefined,
      attachments,
    });
  };

  const handleEditTask = (
    taskId: string,
    values: MaintenanceTaskFormValues,
    attachments: string[]
  ) => {
    updateTask.mutate({
      id: taskId,
      task_name: values.task_name,
      due_date: values.due_date || undefined,
      due_at_mileage: values.due_at_mileage || undefined,
      description: values.description || undefined,
      attachments,
    });
  };

  const handleDeleteTask = (taskId: string) => {
    deleteTask.mutate(taskId);
  };

  const handleToggleComplete = (id: string, isCompleted: boolean) => {
    toggleComplete.mutate({ id, is_completed: isCompleted });
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Maintenance</h1>
          <p className="text-muted-foreground mt-1">
            Track and schedule maintenance tasks for your fleet
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : vehicles && vehicles.length > 0 ? (
          <div className="space-y-6">
            {vehicles.map((vehicle) => (
              <VehicleMaintenanceTimeline
                key={vehicle.id}
                vehicle={vehicle}
                tasks={getTasksForVehicle(vehicle.id)}
                onAddTask={handleAddTask}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onToggleComplete={handleToggleComplete}
                isAddingTask={addTask.isPending}
                isEditingTask={updateTask.isPending}
                isDeletingTask={deleteTask.isPending}
                uploadAttachment={uploadAttachment}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 rounded-full bg-muted mb-4">
              <Wrench className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-1">No vehicles yet</h3>
            <p className="text-muted-foreground">
              Add vehicles first to schedule maintenance tasks.
            </p>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Maintenance;
